import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { User, ChevronUp, ChevronDown, Award } from 'lucide-react';
import { formatPercent } from '@/i18n/locale';
import type { DialerCounters } from '@/api/endpoints/analytics';

/**
 * The per-agent table of the Performances page. Since W4 its rows come from
 * the server (byAgent of POST /api/analytics/dialer/performance), computed on
 * every call of the period: it used to recount the 500 calls the browser had
 * loaded. Conversion is meetings / connected (CEO-7), as everywhere.
 */

interface AgentSummary {
    email: string;
    name?: string;
    full_name?: string;
}

interface AgentPerformanceTableProps {
    /** agentEmail is absent for a viewer (end client): the row is keyed by its rank then. */
    rows?: Array<DialerCounters & { agentEmail?: string; agentName: string }>;
    /** The organisation's members, for display names. */
    agents?: AgentSummary[];
    /** The period's totals (the footer row). */
    totals?: DialerCounters | null;
}

interface AgentStat {
    email: string;
    full_name: string;
    name: string;
    totalCalls: number;
    dialsPerDay: number;
    connectedCalls: number;
    connectionRate: number;
    noAnswer: number;
    voicemails: number;
    callbacks: number;
    callbackRate: number;
    conversations: number;
    meetings: number;
    conversionRate: number;
    talkTimeSeconds: number;
    avgTalkTimeSeconds: number;
    score: number;
}

type SortColumn = keyof AgentStat;
type SortDirection = 'asc' | 'desc';

const formatDuration = (seconds: number): string => {
    if (!seconds || seconds === 0) return '0m';
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    if (h > 0) return `${h}h ${m}m`;
    return `${m}m`;
};

function toStat(c: DialerCounters, email: string, name: string): AgentStat {
    const connectionRate = c.total > 0 ? (c.connected / c.total) * 100 : 0;
    const conversionRate = c.connected > 0 ? (c.meetings / c.connected) * 100 : 0;
    return {
        email,
        full_name: name,
        name,
        totalCalls: c.total,
        dialsPerDay: c.activeDays > 0 ? Math.round(c.total / c.activeDays) : 0,
        connectedCalls: c.connected,
        connectionRate,
        noAnswer: c.noAnswer,
        voicemails: c.voicemails,
        callbacks: c.callbacks,
        callbackRate: c.total > 0 ? (c.callbacks / c.total) * 100 : 0,
        conversations: c.conversations,
        meetings: c.meetings,
        conversionRate,
        talkTimeSeconds: c.talkSeconds,
        avgTalkTimeSeconds: c.connected > 0 ? Math.floor(c.talkSeconds / c.connected) : 0,
        score: (connectionRate * 0.4) + (conversionRate * 0.6),
    };
}

export default function AgentPerformanceTable({ rows = [], agents = [], totals: periodTotals = null }: AgentPerformanceTableProps) {
    const { t } = useTranslation(['pages', 'dialer']);
    const [sortBy, setSortBy] = useState<SortColumn>('totalCalls');
    const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

    const names = new Map(agents.map((a) => [a.email, a.full_name || a.name || a.email]));
    const agentStats: AgentStat[] = rows
        .filter((r) => r.total > 0)
        .map((r, i) => toStat(r, r.agentEmail ?? `#${i}`, (r.agentEmail && names.get(r.agentEmail)) || r.agentName || r.agentEmail || t('dialer:analytics.noAgent')));

    const handleSort = (column: SortColumn) => {
        if (sortBy === column) {
            setSortDirection(prev => prev === 'desc' ? 'asc' : 'desc');
        } else {
            setSortBy(column);
            setSortDirection('desc');
        }
    };

    const sortedAgents = [...agentStats].sort((a, b) => {
        const aVal = a[sortBy] ?? 0;
        const bVal = b[sortBy] ?? 0;
        const comparison = typeof aVal === 'string' && typeof bVal === 'string'
            ? aVal.localeCompare(bVal)
            : Number(bVal) - Number(aVal);
        return sortDirection === 'asc' ? -comparison : comparison;
    });

    const topPerformer = agentStats.length > 0
        ? agentStats.reduce<AgentStat>((best, a) => a.score > best.score ? a : best, agentStats[0]!)
        : null;

    const totals = periodTotals ? toStat(periodTotals, '', '') : null;

    interface ColumnDef {
        key: string;
        label: string;
        sortKey: SortColumn;
        align?: 'left' | 'right';
    }
    const columns: ColumnDef[] = [
        { key: 'name', label: t('pages:analytics.agentPerformanceTable.columns.agent'), sortKey: 'name' as SortColumn, align: 'left' },
        { key: 'totalCalls', label: t('pages:analytics.agentPerformanceTable.columns.calls'), sortKey: 'totalCalls' },
        { key: 'dialsPerDay', label: t('pages:analytics.agentPerformanceTable.columns.callsPerDay'), sortKey: 'dialsPerDay' },
        { key: 'connectedCalls', label: t('pages:analytics.agentPerformanceTable.columns.connections'), sortKey: 'connectedCalls' },
        { key: 'connectionRate', label: t('pages:analytics.agentPerformanceTable.columns.connectionRate'), sortKey: 'connectionRate' },
        { key: 'noAnswer', label: t('pages:analytics.agentPerformanceTable.columns.noAnswer'), sortKey: 'noAnswer' },
        { key: 'voicemails', label: t('pages:analytics.agentPerformanceTable.columns.voicemails'), sortKey: 'voicemails' },
        { key: 'callbacks', label: t('pages:analytics.agentPerformanceTable.columns.callbacks'), sortKey: 'callbacks' },
        { key: 'conversations', label: t('pages:analytics.agentPerformanceTable.columns.conversations'), sortKey: 'conversations' },
        { key: 'meetings', label: t('pages:analytics.agentPerformanceTable.columns.meetings'), sortKey: 'meetings' },
        { key: 'conversionRate', label: t('pages:analytics.agentPerformanceTable.columns.conversionRate'), sortKey: 'conversionRate' },
        { key: 'talkTime', label: t('pages:analytics.agentPerformanceTable.columns.talkTime'), sortKey: 'talkTimeSeconds' },
        { key: 'avgTalkTime', label: t('pages:analytics.agentPerformanceTable.columns.avgTalkTime'), sortKey: 'avgTalkTimeSeconds' },
    ];

    const SortIcon = ({ column }: { column: SortColumn }) => {
        if (sortBy !== column) return <ChevronDown className="w-3 h-3 text-muted-foreground/40" />;
        return sortDirection === 'desc'
            ? <ChevronDown className="w-3 h-3 text-[var(--vf-accent-text)]" />
            : <ChevronUp className="w-3 h-3 text-[var(--vf-accent-text)]" />;
    };

    // Aucun agent : on ne rend RIEN. La carte vide occupait la hauteur d'un
    // tableau pour dire « aucune donnee » a cote de quatre chiffres qui le
    // disaient deja.
    if (sortedAgents.length === 0) return null;

    return (
        <div className="bg-card rounded-xl border border-border">
            <div className="p-6 border-b border-border">
                <h3 className="text-base font-medium text-foreground">{t('pages:analytics.agentPerformanceTable.title')}</h3>
            </div>
            {(
                <div className="overflow-x-auto vf-scroll-visible">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="border-b border-border/50">
                                {columns.map(col => (
                                    <th
                                        key={col.key}
                                        scope="col"
                                        aria-sort={sortBy === col.sortKey ? (sortDirection === 'asc' ? 'ascending' : 'descending') : 'none'}
                                        onClick={() => handleSort(col.sortKey)}
                                        className={`px-4 py-3 font-medium text-muted-foreground text-xs cursor-pointer hover:text-foreground transition-colors whitespace-nowrap ${
                                            col.align === 'left' ? 'text-left' : 'text-right'
                                        }`}
                                    >
                                        <div className={`flex items-center gap-1 ${col.align === 'left' ? '' : 'justify-end'}`}>
                                            {col.label}
                                            <SortIcon column={col.sortKey} />
                                        </div>
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {sortedAgents.map((agent) => {
                                const isTop = agent.email === topPerformer?.email;
                                return (
                                    <tr
                                        key={agent.email}
                                        className={`border-b border-border/30 transition-colors ${
                                            isTop ? 'bg-[var(--vf-accent-bg)]' : 'hover:bg-accent/50'
                                        }`}
                                    >
                                        <td className="px-4 py-3 text-left">
                                            <div className="flex items-center gap-2.5">
                                                <div className="bg-[var(--vf-accent-bg)] w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0">
                                                    <User className="w-4 h-4 text-[var(--vf-accent-text)]" />
                                                </div>
                                                <div className="min-w-0">
                                                    <div className="flex items-center gap-1.5">
                                                        <span className="font-medium text-foreground text-sm truncate">
                                                            {agent.full_name || agent.email}
                                                        </span>
                                                        {isTop && <Award className="w-3.5 h-3.5 text-amber-500 flex-shrink-0" />}
                                                    </div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-4 py-3 text-right font-semibold text-foreground">{agent.totalCalls}</td>
                                        <td className="px-4 py-3 text-right text-foreground/80">{agent.dialsPerDay}</td>
                                        <td className="px-4 py-3 text-right text-foreground/80">{agent.connectedCalls}</td>
                                        <td className="px-4 py-3">
                                            <div className="flex items-center gap-2 justify-end">
                                                <span className={`font-medium text-xs ${
                                                    agent.connectionRate >= 30 ? 'text-green-600' :
                                                    agent.connectionRate >= 15 ? 'text-amber-600' :
                                                    'text-muted-foreground'
                                                }`}>
                                                    {formatPercent(agent.connectionRate)}
                                                </span>
                                                <div className="w-16 h-1.5 bg-muted rounded-full overflow-hidden">
                                                    <div
                                                        className={`h-full rounded-full transition-all ${
                                                            agent.connectionRate >= 30 ? 'bg-green-500' :
                                                            agent.connectionRate >= 15 ? 'bg-amber-500' :
                                                            'bg-muted-foreground/60'
                                                        }`}
                                                        style={{ width: `${Math.min(agent.connectionRate, 100)}%` }}
                                                    />
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-4 py-3 text-right text-muted-foreground">{agent.noAnswer}</td>
                                        <td className="px-4 py-3 text-right text-muted-foreground">{agent.voicemails}</td>
                                        <td className="px-4 py-3 text-right text-foreground/80">{agent.callbacks}</td>
                                        <td className="px-4 py-3 text-right font-medium text-foreground">{agent.conversations}</td>
                                        <td className="px-4 py-3 text-right">
                                            <span className="font-semibold text-[var(--vf-accent-text)]">{agent.meetings}</span>
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className="flex items-center gap-2 justify-end">
                                                <span className={`font-medium text-xs ${
                                                    agent.conversionRate >= 5 ? 'text-green-600' :
                                                    agent.conversionRate >= 2 ? 'text-amber-600' :
                                                    'text-muted-foreground'
                                                }`}>
                                                    {formatPercent(agent.conversionRate)}
                                                </span>
                                                <div className="w-16 h-1.5 bg-muted rounded-full overflow-hidden">
                                                    <div
                                                        className={`h-full rounded-full transition-all ${
                                                            agent.conversionRate >= 5 ? 'bg-green-500' :
                                                            agent.conversionRate >= 2 ? 'bg-amber-500' :
                                                            'bg-muted-foreground/60'
                                                        }`}
                                                        style={{ width: `${Math.min(agent.conversionRate * 5, 100)}%` }}
                                                    />
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-4 py-3 text-right text-foreground/80">{formatDuration(agent.talkTimeSeconds)}</td>
                                        <td className="px-4 py-3 text-right text-foreground/80">{formatDuration(agent.avgTalkTimeSeconds)}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                        {totals && (
                        <tfoot>
                            <tr className="surface-1 border-t border-border">
                                <td className="px-4 py-3 text-left font-semibold text-foreground text-sm">{t('pages:analytics.agentPerformanceTable.total')}</td>
                                <td className="px-4 py-3 text-right font-bold text-foreground">{totals.totalCalls}</td>
                                <td className="px-4 py-3 text-right font-medium text-foreground/80">{totals.dialsPerDay}</td>
                                <td className="px-4 py-3 text-right font-medium text-foreground/80">{totals.connectedCalls}</td>
                                <td className="px-4 py-3 text-right font-medium text-foreground/80">{formatPercent(totals.connectionRate)}</td>
                                <td className="px-4 py-3 text-right text-muted-foreground">{totals.noAnswer}</td>
                                <td className="px-4 py-3 text-right text-muted-foreground">{totals.voicemails}</td>
                                <td className="px-4 py-3 text-right text-foreground/80">{totals.callbacks}</td>
                                <td className="px-4 py-3 text-right font-medium text-foreground">{totals.conversations}</td>
                                <td className="px-4 py-3 text-right font-bold text-[var(--vf-accent-text)]">{totals.meetings}</td>
                                <td className="px-4 py-3 text-right font-medium text-foreground/80">{formatPercent(totals.conversionRate)}</td>
                                <td className="px-4 py-3 text-right text-foreground/80">{formatDuration(totals.talkTimeSeconds)}</td>
                                <td className="px-4 py-3 text-right text-foreground/80">{formatDuration(totals.avgTalkTimeSeconds)}</td>
                            </tr>
                        </tfoot>
                        )}
                    </table>
                </div>
            )}
        </div>
    );
}
